<template>
  <div>
    <div
      v-if="isFieldVisible(fields.totalDegreeMapCredits)"
      class="flex-1"
    >
      <label class="font-medium">{{ fields.totalDegreeMapCredits.label }}</label>
      <div>{{ totalActualCredits || '-' }}</div>
    </div>
    <div class="flex mt-4">
      <div
        v-if="isFieldVisible(fields.degreeMapEffectiveCatalogYear)"
        class="flex-1 mr-6"
      >
        <label class="font-medium">{{ fields.degreeMapEffectiveCatalogYear.label }}</label>
        <div>{{ `${getTermDisplayName(documentValue.startTerm || {})} - ${getTermDisplayName(documentValue.endTerm || {})}` }}</div>
      </div>
      <div
        v-if="isFieldVisible(fields.actualCredits)"
        class="flex-1"
      >
        <label class="font-medium">{{ fields.actualCredits.label }}</label>
        <div>{{ totalActualCreditsSemester }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import _minBy from 'lodash/minBy'
import _maxBy from 'lodash/maxBy'
import _isObject from 'lodash/isObject'
import _isNumber from 'lodash/isNumber'

export default {
  props: {
    semester: {
      type: Object,
      default: () => ({}),
    },
    allRequirements: {
      type: Array,
      default: () => [],
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    terms: {
      type: Array,
      default: () => [],
    },
    semesterNames: {
      type: Object,
      default: () => ({}),
    },
    courses: {
      type: Array,
      default: () => [],
    },
    courseSets: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    fields() {
      return this.config.fields
    },
    totalActualCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements,
        'creditHours',
        'actualCredits'
      )
    },
    totalActualCreditsSemester() {
      return this.getTotalCreditsOutput(
        this.semester.requirements,
        'creditHours',
        'actualCredits'
      )
    },
  },
  methods: {
    courseByGroupId(courseGroupId) {
      return (
        this.courses.find(
          (course) =>
            course.courseGroupId === courseGroupId ||
            course.deprecatedCourseGroupId === courseGroupId
        ) || {}
      )
    },
    getCourseSetByCourseGroupId(courseSetGroupId) {
      return this.courseSets.find((s) => s.courseSetGroupId === courseSetGroupId)
    },
    getTermDisplayName(term = {}) {
      if (!term.displayName) {
        term = this.terms.find((el) => el.id === term.id) || term
      }
      return (
        term.displayName ||
        (term.semester && term.year
          ? [this.semesterNames[term.semester], term.year].join(' ')
          : '')
      )
    },
    isFieldVisible(field) {
      return field && field?.hidden !== true
    },
    filterRequirements(requirements) {
      return requirements.filter(
        (requirement) =>
          requirement.requirementSelect &&
          Array.isArray(requirement.requirementSelect)
      )
    },
    getCourseCreditMinMaxValue(courseId, creditType) {
      const course = this.courseByGroupId(courseId)
      const { min, max, value } =
        (course && course.credits && course.credits[creditType]) || {}
      return this.normalizeMinMax({ min, max, value })
    },
    getSingleOrMultipleCoursesCreditMinMaxValue(courseIds, creditType) {
      if (Array.isArray(courseIds)) {
        const min = courseIds
          .map(
            (c) =>
              this.normalizeMinMax(
                this.getCourseCreditMinMaxValue(c, creditType)
              ).min
          )
          .reduce((a, b) => a + (b || 0), 0)
        const max = courseIds
          .map(
            (c) =>
              this.normalizeMinMax(
                this.getCourseCreditMinMaxValue(c, creditType)
              ).max
          )
          .reduce((a, b) => a + (b || 0), 0)
        return { min, max }
      }
      return this.getCourseCreditMinMaxValue(courseIds, creditType)
    },
    getRequirementCreditValues(requirement, creditType, creditKey) {
      if (_isObject(requirement[creditKey]))
        return this.normalizeMinMax(requirement[creditKey])
      const creditRange = {}
      requirement.requirementSelect.forEach((item) => {
        if (['course', 'courses', 'courseSets'].includes(item.type)) {
          if (item.type === 'courseSets') {
            const resource = this.getCourseSetByCourseGroupId(item.value)
            const courseSetCredits = resource?.credits?.[creditType] || {};
            const actualCredits = resource?.actualCredits || {};
            const { min, max, value } = { ...courseSetCredits, ...actualCredits } 
            const normalizeMinMax = this.normalizeMinMax({ min, max, value })
            if (_isNumber(normalizeMinMax.min)) {
              creditRange.min = Math.min(
                creditRange.min || Number.POSITIVE_INFINITY,
                normalizeMinMax.min
              )
            }
            if (_isNumber(normalizeMinMax.max)) {
              creditRange.max = Math.max(
                creditRange.max || Number.NEGATIVE_INFINITY,
                normalizeMinMax.max
              )
            }
          } else {
            const {
              min,
              max,
            } = this.getSingleOrMultipleCoursesCreditMinMaxValue(
              item.value,
              creditType
            )
            creditRange.min = Math.min(
              creditRange.min || Number.POSITIVE_INFINITY,
              min
            )
            creditRange.max = Math.max(
              creditRange.max || Number.NEGATIVE_INFINITY,
              max
            )
          }
        }
      })
      return creditRange
    },
    getRequirementCreditRange(creditValues) {
      let creditRange = {}
      if (Array.isArray(creditValues)) {
        const { min } =
          _minBy(creditValues.filter((v) => _isNumber(v.min)), (c) => c.min) ||
          {}
        const { max } =
          _maxBy(creditValues.filter((v) => _isNumber(v.max)), (c) => c.max) ||
          {}
        Object.assign(creditRange, { min, max })
      } else {
        creditRange = creditValues
      }
      return creditRange
    },
    normalizeMinMax({ min, max, value }) {
      if (_isNumber(value)) return { min: value, max: value }
      if (!_isNumber(min) && _isNumber(max)) return { min: max, max }
      if (!_isNumber(max) && _isNumber(min)) return { max: min, min }
      return { min, max }
    },
    outputCreditRange({ min, max }) {
      return min === max
        ? min
        : [min, max].filter((v) => _isNumber(v)).join(' - ') || ''
    },
    getTotalCreditsOutput(requirements, creditType, creditKey) {
      const totalCredits = this.filterRequirements(requirements).reduce(
        (acc, r) => {
          const creditValues = this.getRequirementCreditValues(
            r,
            creditType,
            creditKey
          )
          const creditRange = this.getRequirementCreditRange(creditValues)
          acc.min += creditRange.min || 0
          acc.max += creditRange.max || 0
          return acc
        },
        { min: 0, max: 0 }
      )
      return this.outputCreditRange(totalCredits)
    },
  },
}
</script>
